import React, { useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import "../../scss/clubLogosBar.scss";
import { useI18n } from "../../i18n/I18nProvider";

interface ClubLogoItem {
    id: number;
    name: string;
    role: string;
    image: string;
}

const CLUB_LOGOS: ClubLogoItem[] = [
    {
        id: 1,
        name: "AEGIS",
        role: "Advanced Exploration Group for Information Security",
        image: process.env.PUBLIC_URL + "/aegis_biale.png", 
    },
    {
        id: 2,
        name: "CONECT",
        role: "Community of Networking Engineers and Computing Technologies",
        image: process.env.PUBLIC_URL + "/CONECTLOGObc.png", 
    },
    {
        id: 3,
        name: "NODE",
        role: "Networked Objects & Device Engineering",
        image: process.env.PUBLIC_URL + "/nodelogobc.png", 
    },
];

interface ClubLogosBarProps {
    speed?: number;
    pauseOnHover?: boolean;
    showHeader?: boolean;
}

const BASE_SPEED = 35;

const ClubLogosBar: React.FC<ClubLogosBarProps> = ({
    speed = BASE_SPEED,
    pauseOnHover = true,
    showHeader = false,
}) => {
    const navigate = useNavigate();
    const { t } = useI18n();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const drag = useRef({
        active: false,
        pending: false,
        startX: 0,
        lastX: 0,
        inputType: "mouse",
        didDrag: false,
        anims: [] as { anim: Animation; t0: number; dur: number; w: number }[],
    });

    const displayLogos = [...CLUB_LOGOS, ...CLUB_LOGOS, ...CLUB_LOGOS];

    const handleLogoClick = (clubName: string) => {
        if (drag.current.didDrag) return;
        navigate(`/kola-naukowe#${clubName}`);
    };

    const gatherAnims = useCallback(() => {
        const w = wrapperRef.current;
        if (!w) return [];
        const result: typeof drag.current.anims = [];
        w.querySelectorAll(".rfm-marquee").forEach((el) => {
            el.getAnimations().forEach((a) => {
                const dur = (a.effect?.getComputedTiming().duration as number) ?? 0;
                if (dur > 0) {
                    result.push({
                        anim: a,
                        t0: (a.currentTime as number) ?? 0,
                        dur,
                        w: (el as HTMLElement).offsetWidth,
                    });
                }
            });
        });
        return result;
    }, []);

    const beginDrag = useCallback((clientX: number, inputType: "mouse" | "touch") => {
        drag.current = {
            active: false,
            pending: true,
            startX: clientX,
            lastX: clientX,
            inputType,
            didDrag: false,
            anims: [],
        };
    }, []);

    const moveDrag = useCallback((clientX: number) => {
        const d = drag.current;
        if (!d.pending && !d.active) return false;
        const dx = clientX - d.startX;
        const activationThreshold = d.inputType === "mouse" ? 5 : 4;

        if (d.pending && Math.abs(dx) > activationThreshold) {
            const anims = gatherAnims();
            if (!anims.length) {
                d.pending = false;
                return false;
            }
            anims.forEach(({ anim }) => anim.pause());
            d.anims = anims.map((item) => ({
                ...item,
                t0: (item.anim.currentTime as number) ?? item.t0,
            }));
            d.active = true;
            d.pending = false;
            d.didDrag = true;
            d.startX = clientX;
            d.lastX = clientX;
            return true;
        }

        if (d.active) {
            const deltaX = clientX - d.lastX;
            d.lastX = clientX;
            d.anims.forEach(({ anim, dur, w }) => {
                const currentTime = (anim.currentTime as number) ?? 0;
                const shift = (deltaX / w) * dur;
                anim.currentTime = ((currentTime - shift) % dur + dur) % dur;
            });
            return true;
        }

        return false;
    }, [gatherAnims]);

    const endDrag = useCallback(() => {
        const d = drag.current;
        if (d.active) {
            d.anims.forEach(({ anim }) => anim.play());
        }
        d.active = false;
        d.pending = false;
        d.anims = [];
        d.lastX = 0;
    }, []);

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        if (e.touches.length !== 1) return;
        beginDrag(e.touches[0].clientX, "touch");
    }, [beginDrag]);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        if (!e.touches.length) return;
        if (moveDrag(e.touches[0].clientX)) {
            e.preventDefault();
        }
    }, [moveDrag]);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        beginDrag(e.clientX, "mouse");
    }, [beginDrag]);

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        if (moveDrag(e.clientX)) {
            e.preventDefault();
        }
    }, [moveDrag]);

    const onClickCapture = useCallback((e: React.MouseEvent) => {
        if (drag.current.didDrag) {
            e.preventDefault();
            e.stopPropagation();
            drag.current.didDrag = false;
        }
    }, []);

    return (
        <section
            className="club-logos-bar"
            aria-label={t("clubs_bar_label")}
        >
            {showHeader && (
                <header className="club-logos-bar__header">
                    <h2 className="club-logos-bar__title">
                        {t("clubs_bar_title_prefix")}<span>{t("clubs_bar_title_highlight")}</span>
                    </h2>
                    <div className="club-logos-bar__divider" />
                    <p className="club-logos-bar__subtitle">
                        {t("clubs_bar_subtitle")}
                    </p>
                </header>
            )}

            <div
                className="club-logos-bar__marquee-wrap"
                ref={wrapperRef}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={endDrag}
                onTouchCancel={endDrag}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onClickCapture={onClickCapture}
                onDragStart={(e) => e.preventDefault()}
            >
                <Marquee
                    className="club-logos-bar__marquee"
                    pauseOnHover={true}
                    gradient={false}
                    speed={speed}
                    direction="left"
                    autoFill={true}
                >
                    {displayLogos.map((logo, index) => (
                        <article
                            key={`${logo.id}-${index}`}
                            className="club-logos-tile club-logos-tile--clickable"
                            aria-label={`${t("clubs_bar_club_label")} ${logo.name}`}
                            onClick={() => handleLogoClick(logo.name)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleLogoClick(logo.name);
                                }
                            }}
                        >
                            <div className="club-logos-tile__glow" />

                            <div className="club-logos-tile__content">
                                <div className="club-logos-tile__logo-box">
                                    <img src={logo.image} alt={`${t("clubs_bar_logo_alt")} ${logo.name}`} draggable={false} />
                                </div>

                                <div className="club-logos-tile__text">
                                    <h3 className="club-logos-tile__name">{logo.name}</h3>
                                    <p className="club-logos-tile__role">{logo.role}</p>
                                </div>
                            </div>
                        </article>
                    ))}
                </Marquee>
            </div>
        </section>
    );
};

export { CLUB_LOGOS };
export type { ClubLogoItem };
export default ClubLogosBar;